import React, { useCallback, useEffect, useRef } from 'react';
import SafeIcon from './SafeIcon';
import {
  contentForEditor,
  convertPasteToHtml,
  normalizeEditorOutput,
} from '../../utils/richText';

function ToolbarButton({ title, onClick, disabled, children, active = false }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      className={`px-2 py-1 rounded-lg text-[11px] font-bold transition-colors disabled:opacity-40 ${
        active
          ? 'bg-brand-green/15 text-brand-navy'
          : 'text-[var(--text-secondary)] hover:bg-[var(--border-subtle)] hover:text-[var(--text-main)]'
      }`}
    >
      {children}
    </button>
  );
}

export default function RichTextField({
  value = '',
  onChange,
  disabled = false,
  placeholder = '',
  className = '',
  minHeight = '120px',
}) {
  const editorRef = useRef(null);
  const lastValueRef = useRef(undefined);

  const emitChange = useCallback(() => {
    if (!editorRef.current || !onChange) return;
    const normalized = normalizeEditorOutput(editorRef.current.innerHTML);
    lastValueRef.current = normalized;
    onChange(normalized);
  }, [onChange]);

  useEffect(() => {
    if (!editorRef.current) return;
    if (value === lastValueRef.current) return;
    lastValueRef.current = value;
    editorRef.current.innerHTML = contentForEditor(value);
  }, [value]);

  const exec = (command, arg = null) => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    emitChange();
  };

  const handlePaste = (e) => {
    if (disabled) return;
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    const converted = convertPasteToHtml(html, text);

    editorRef.current?.focus();
    if (converted) {
      document.execCommand('insertHTML', false, converted);
    } else if (text) {
      document.execCommand('insertText', false, text);
    }
    emitChange();
  };

  const isEmpty = !value || !value.trim();

  return (
    <div className={`rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] overflow-hidden focus-within:border-brand-green focus-within:ring-1 focus-within:ring-brand-green transition-all ${className}`}>
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-[var(--border-subtle)] bg-[var(--bg-base)]/50">
        <ToolbarButton title="Negrita" disabled={disabled} onClick={() => exec('bold')}>
          <span className="font-black">B</span>
        </ToolbarButton>
        <ToolbarButton title="Cursiva" disabled={disabled} onClick={() => exec('italic')}>
          <span className="italic font-serif">I</span>
        </ToolbarButton>
        <span className="w-px h-4 bg-[var(--border-subtle)] mx-0.5" />
        <ToolbarButton title="Encabezado 2" disabled={disabled} onClick={() => exec('formatBlock', 'h2')}>
          H2
        </ToolbarButton>
        <ToolbarButton title="Encabezado 3" disabled={disabled} onClick={() => exec('formatBlock', 'h3')}>
          H3
        </ToolbarButton>
        <span className="w-px h-4 bg-[var(--border-subtle)] mx-0.5" />
        <ToolbarButton title="Lista con viñetas" disabled={disabled} onClick={() => exec('insertUnorderedList')}>
          <SafeIcon name="List" className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Lista numerada" disabled={disabled} onClick={() => exec('insertOrderedList')}>
          1.
        </ToolbarButton>
        <ToolbarButton title="Cita" disabled={disabled} onClick={() => exec('formatBlock', 'blockquote')}>
          <SafeIcon name="MessageSquare" className="w-3.5 h-3.5" />
        </ToolbarButton>
      </div>

      <div className="relative">
        {isEmpty && placeholder && (
          <div
            className="absolute top-3 left-4 text-sm text-[var(--text-secondary)]/50 pointer-events-none select-none"
            aria-hidden
          >
            {placeholder}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={emitChange}
          onPaste={handlePaste}
          onBlur={emitChange}
          className="gfi-rich-content gfi-rich-editor w-full py-3 px-4 text-sm text-[var(--text-main)] outline-none overflow-y-auto resize-y"
          style={{ minHeight }}
        />
      </div>
    </div>
  );
}
